import React, { useEffect } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Chip,
  Skeleton,
  useTheme
} from '@mui/material';
import {
  Campaign,
  Policy,
  Notifications,
  ArrowForward,
  PushPin
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useOrganizationFeedStore from '@/store/OrganizationFeedStore';

const categoryConfig = {
  announcement: {
    label: 'Announcement',
    color: 'primary' as const,
    icon: <Campaign sx={{ fontSize: 14 }} />
  },
  policy_update: {
    label: 'Policy',
    color: 'warning' as const,
    icon: <Policy sx={{ fontSize: 14 }} />
  },
  general_notice: {
    label: 'Notice',
    color: 'info' as const,
    icon: <Notifications sx={{ fontSize: 14 }} />
  }
};

const priorityColors = {
  low: '#9e9e9e',
  normal: '#2196f3',
  high: '#ff9800',
  urgent: '#f44336'
};

interface FeedWidgetProps {
  limit?: number;
}

const FeedWidget: React.FC<FeedWidgetProps> = ({ limit = 3 }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { recentPosts, fetchRecentPosts, loading } = useOrganizationFeedStore();

  useEffect(() => {
    fetchRecentPosts(limit);
  }, [limit]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const truncateContent = (content: string, maxLength: number = 80) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength).trim() + '...';
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Campaign sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
              Organization Feed
            </Typography>
          </Box>
          <Button
            size="small"
            endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
            onClick={() => navigate('/organization-feed')}
            sx={{ textTransform: 'none', fontSize: '0.8rem' }}
          >
            View All
          </Button>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[1, 2, 3].map((i) => (
                <Box key={i}>
                  <Skeleton variant="text" width="30%" height={20} />
                  <Skeleton variant="text" width="80%" height={24} />
                  <Skeleton variant="text" width="60%" height={18} />
                </Box>
              ))}
            </Box>
          ) : recentPosts.length === 0 ? (
            <Box sx={{
              textAlign: 'center',
              py: 4,
              color: theme.palette.text.secondary
            }}>
              <Campaign sx={{ fontSize: 40, opacity: 0.5, mb: 1 }} />
              <Typography variant="body2">
                No recent announcements
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {recentPosts.map((post) => {
                const category = categoryConfig[post.category];
                return (
                  <Box
                    key={post.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      backgroundColor: theme.palette.action.hover,
                      borderLeft: `3px solid ${priorityColors[post.priority]}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.action.selected,
                        transform: 'translateX(4px)'
                      }
                    }}
                    onClick={() => navigate('/organization-feed')}
                  >
                    {/* Tags */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      {post.is_pinned && (
                        <PushPin sx={{ fontSize: 12, color: theme.palette.warning.main }} />
                      )}
                      <Chip
                        icon={category.icon}
                        label={category.label}
                        size="small"
                        color={category.color}
                        variant="outlined"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          '& .MuiChip-label': { px: 0.5 },
                          '& .MuiChip-icon': { fontSize: 10, ml: 0.5 }
                        }}
                      />
                      {post.priority === 'urgent' && (
                        <Chip
                          label="Urgent"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            backgroundColor: theme.palette.error.main,
                            color: '#fff',
                            '& .MuiChip-label': { px: 0.5 }
                          }}
                        />
                      )}
                    </Box>

                    {/* Title */}
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        lineHeight: 1.3,
                        mb: 0.25
                      }}
                    >
                      {post.title}
                    </Typography>

                    {/* Preview */}
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        display: 'block',
                        lineHeight: 1.4,
                        mb: 0.5
                      }}
                    >
                      {truncateContent(post.content)}
                    </Typography>

                    {/* Footer */}
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.disabled,
                        fontSize: '0.7rem'
                      }}
                    >
                      {post.author_name} • {formatDate(post.created_at)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default FeedWidget;
